<script setup lang="ts">
import { useWordProcessorStore } from "~/components/Base/WordProcessor/useWordProcessorStore";
import { useClearInstanceErrors } from "~/composables/selfAssessmentValidation/useClearInstanceErrors";
import { useSelfAssessmentStepValidation } from "~/composables/selfAssessmentValidation/useSelfAssessmentStepValidation";

const emit = defineEmits(["onSubmitted"]);

const documentId = inject("documentId") as any;
const documentDiagnosticsStore = useDocumentDiagnosticsStore(documentId);
const { diagnosticsMap, diagnosticsConfiguration } = storeToRefs(
  documentDiagnosticsStore
);

const submittedDocumentStore = useSubmittedDocumentStore(documentId);
const { revision, hasChecklist, submittedDocument } = storeToRefs(
  submittedDocumentStore
);

const documentViewModeStore = useDocumentViewModeStore(documentId);
const { isCompanionMode } = storeToRefs(documentViewModeStore);

const { access: writingToolsAccess, isAccessLoading } =
  useStudentWritingToolsAccess(documentId);
const isWritingToolsAccessLoading = computed(
  () =>
    isCompanionMode.value != true &&
    submittedDocument.value?.enableStudentReview != true &&
    isAccessLoading.value
);

const documentSelfAssessmentStore = useDocumentSelfAssessmentStore(documentId);
const {
  selfAssessmentConfiguration,
  isRunningAssessment,
  activeStepIndex,
  showBadge,
  
  stepCriteriaAttributes,
  stepExpectedInstances,
  headerSteps,
  hasRunAssessment,
  generalError,
  preCheckSteps,
  guidedReviewSteps,
  baseMessageSteps,
  relevantInstances,
  allSteps,
} = storeToRefs(documentSelfAssessmentStore);

const wordProcessorStore = useWordProcessorStore(documentId);
const { editorRef } = storeToRefs(wordProcessorStore);

const documentTokenSelectorStore = useDocumentTokenSelectorStore(documentId);

// Helper function to convert ops to token ranges with visual coordinates
const convertOpsToTokenRanges = (ops: any[]) => {


  const quill = wordProcessorStore.getQuillInstance();
  if (!quill) {
    return [];
  }

  const tokenRanges: any[] = [];
  let currentPosition = 0;

  // Iterate through ops to find positions
  for (const op of ops) {
    if (typeof op.insert === "string") {
      const insertLength = op.insert.length;

      // Check if this op has the submissionPart attribute we're looking for
      if (op.attributes?.submissionPart) {
        // Get bounding rect from Quill for this range
        const bounds = quill.getBounds(currentPosition, insertLength);

        if (bounds && bounds.width > 0 && bounds.height > 0) {
          // Convert relative bounds to absolute viewport coordinates
          const editorRect = quill.root.getBoundingClientRect();
          tokenRanges.push({
            id: useGenerateUID(),
            x: editorRect.left + bounds.left,
            y: editorRect.top + bounds.top,
            width: bounds.width,
            height: bounds.height,
            rangeStart: currentPosition,
            rangeEnd: currentPosition + insertLength,
            criteriaIdentifier: op.attributes.submissionPart.criteriaIdentifier,
            relevantInstanceIdentifier:
              op.attributes.submissionPart.relevantInstanceIdentifier,
          });
        }
      }

      currentPosition += insertLength;
    } else if (op.insert && typeof op.insert === "object") {
      // Handle embeds (images, videos, etc.) - they take up 1 character
      currentPosition += 1;
    }
  }

  return tokenRanges;
};

onMounted(async () => {
  if(submittedDocument.value?.state == DocumentState.studentCompleted) {
    return;
  }
});

const showSelfAssessment = computed(() => {
  if (submittedDocument.value == undefined) {
    return false;
  }

  if (
    isCompanionMode.value != true &&
    writingToolsAccess.value.canRunChecklist != true
  ) {
    return false;
  }

  if (submittedDocument.value.assignmentType == AssignmentType.general) {
    return false;
  }

  if (
    submittedDocument.value.documentSubmissionType ==
    DocumentSubmissionType.finalDraft
  ) {
    return true;
  }

  if (
    submittedDocument.value.documentSubmissionType ==
    DocumentSubmissionType.keyWordOutline
  ) {
    return false;
  }

  return (
    submittedDocument.value.documentSubmissionType ==
      DocumentSubmissionType.roughDraft &&
    submittedDocument.value?.assignmentRequireRoughDraftChecklist == true
  );
});

const showSteps = computed(() => {
  if (diagnosticsConfiguration.value.enableBodyParagraphDiagnostics != true)
    return true;

  if (diagnosticsMap.value["paragraphs"] == undefined) return true;

  if (diagnosticsMap.value["paragraphs"].isRunning == true) return false;

  if (diagnosticsMap.value["paragraphs"].hasPassedDiagnostics != true)
    return false;

  return true;
});

const activeStep = computed(() => {
  if (activeStepIndex.value == undefined) return undefined;

  return allSteps.value[activeStepIndex.value];
});

const stepsToShow = computed(() => {
  let steps = [];

  var guidedReviewSteps = allSteps.value.filter(
    (step) => step.isPreCheckStep != true || step.isPreCheckStep == undefined
  );

  // Show the step if the prvious step is compelte:
  for (let i = 0; i < guidedReviewSteps.length; i++) {
    const step = guidedReviewSteps[i];

    // If the current step is not complete, break out of hte loops so we don't show any steps after it
    if (step.isComplete != true) {
      steps.push(step);
      break;
    }

    if (i == 0 || guidedReviewSteps[i - 1].isComplete == true) {
      steps.push(step);
    }
  }

  return steps;
});

// watch(stepsToShow, () => {
//   if (stepsToShow.value.length > 0) {
//     documentSelfAssessmentStore.setActiveStepIndex(
//       stepsToShow.value.length - 1
//     );
//   }
// });

watch(activeStepIndex, () => {
  const quill = wordProcessorStore.getQuillInstance();
  if (!quill) return;

  useClearInstanceErrors(quill.root);
  documentTokenSelectorStore.clearDocumentSelectionType();

  if (activeStep.value == undefined) {
    return;
  }

  documentTokenSelectorStore.setDocumentSelectionType(
    activeStep.value.selectionType,
    (tokenGlobalPosition) => {}
  );

  if (revision.value == undefined) return 0;

  if (revision.value.studentSubmissionOps == undefined) return 0;

  const studentSubmissionOps = revision.value.studentSubmissionOps;

  const stepIdentifier = activeStep.value.identifier;

  const relevantInstancesOps = studentSubmissionOps.filter(
    (op: any) =>
      op.attributes?.submissionPart?.criteriaIdentifier === stepIdentifier
  );

  // Convert all ops (not just filtered ones) to get correct positions
  const tokenRanges = convertOpsToTokenRanges(studentSubmissionOps);

  // Filter to only the token ranges for the active step
  const relevantTokenRanges = tokenRanges.filter(
    (range: any) => range.criteriaIdentifier === stepIdentifier
  );

  useDocumentTokenSelectorStore(documentId).addSelectedRanges(
    relevantTokenRanges,
    {
      start: relevantTokenRanges[0]?.rangeStart,
      end: relevantTokenRanges[relevantTokenRanges.length - 1]?.rangeEnd,
    }
  );
});

const areAllStepsComplete = computed(() => {
  return guidedReviewSteps.value.every((step) => step.isComplete == true);
});

const isSubmitting = ref(false);
const onSubmitWork = async () => {
  isSubmitting.value = true;

  await useSubmitEdittedRevision(documentId, revision.value);

  isSubmitting.value = false;

  emit("onSubmitted");
};



const canProgressStep = () => {
  if (activeStep.value == undefined) return false;

  let quill = wordProcessorStore.getQuillInstance();

  if (!quill) return false;

  var canProgress = useSelfAssessmentStepValidation({
    documentId,
    step: activeStep.value,
    relevantInstances: documentSelfAssessmentStore.stepRelevantInstances(activeStep.value) ?? [],
    quill,
  });

  return canProgress;
};

const wasNextStepManuallyTriggered = ref(false);

const nextStep = async () => {
  if (activeStep.value == undefined) return;

  activeStep.value.showInstancesWarning = false;
  activeStep.value.errorMessage = undefined;

  await nextTick();

  try {
    const response = await documentSelfAssessmentStore.assessStep(
      activeStep.value
    );

    if (response == false) {
      activeStep.value.showInstancesWarning = true;
      return;
    }
  } catch (error: any) {
    activeStep.value.showInstancesWarning = true;
    return;
  }

  wasNextStepManuallyTriggered.value = true;

  activeStep.value.isComplete = true;

  documentSelfAssessmentStore.nextStep();
};

watch([activeStepIndex, relevantInstances], () => {
  if (!wordProcessorStore.getQuillInstance()) return;
  documentSelfAssessmentStore.resetSelection();

  if(submittedDocument.value?.state == DocumentState.editing) {
    return;
  }
  
  documentSelfAssessmentStore.applyStepSelection();
});

const badge = computed(() => {
  return submittedDocument.value?.submissionSeal;
});

const badgeLabel = computed(() => {
  if (badge.value == undefined) return "";

  switch (submittedDocument.value?.submissionSeal) {
    case SubmittedDocumentBadge.gold:
      return "Gold Scroll";
    case SubmittedDocumentBadge.silver:
      return "Silver Scroll";
    case SubmittedDocumentBadge.bronze:
      return "Bronze Scroll";
  }
});

const isFormattingStep = computed(() => {
  if (activeStepIndex.value == -2) {
    return true;
  }

  return false;
});

const isBaseMessageStep = computed(() => {
  if (activeStepIndex.value == -1) {
    return true;
  }

  return false;
});


const isActiveStepPreCheck = computed(() => {
  if (activeStep.value == undefined) return false;
  return activeStep.value.isPreCheckStep == true;
});

const badgeContainer = ref<HTMLElement | null>(null);

const runAssessment = async () => {
  try {
    // Blur the editor
    const quill = wordProcessorStore.getQuillInstance();
    if (quill) {
      quill.blur();
    }

    await documentSelfAssessmentStore.runAssessment();

    if(isCompanionMode.value == true) {
      submittedDocument.value!.state = DocumentState.studentCompleted;
      await useSubmittedDocumentStore(documentId).saveDocumentDebounced();
    }


  } catch (error: any) {
    return;
  }

  if (showBadge.value == true) {
    // Scroll to the badge
    await nextTick();

    // Await 200ms
    await new Promise((resolve) => setTimeout(resolve, 200));

    badgeContainer.value?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }
};

const submitButtonLabel = computed(() => {
  if(isCompanionMode.value == true) {
    return "Complete";
  }

  return submittedDocument.value?.enableSubmissionBadges == true && showBadge.value != true ? "Submit without Changes" : "Submit";
})

const isNavigatingBackToChecklist = ref(false);
const backToChecklist = async () => {
  isNavigatingBackToChecklist.value = true;
  submittedDocument.value!.state = DocumentState.editing;
  await useSubmittedDocumentStore(documentId).saveDocumentDebounced();
  
};

</script>

<template>
  <div class="h-full flex flex-col min-h-0 overflow-y-auto pb-2">
    <!-- DEBUGGER PAGE CONTROLS -->
    <!-- <div class="flex flex-row items-center gap-2">
      <BaseIconButton
        @click="
          documentSelfAssessmentStore.setActiveStepIndex(
            (activeStepIndex ?? 0) - 1
          )
        "
      >
        <BaseIconArrowLeft />
      </BaseIconButton>
      <div>
        <BaseTextInput type="number" v-model="activeStepIndex" />
      </div>
      <BaseIconButton
        @click="
          documentSelfAssessmentStore.setActiveStepIndex(
            (activeStepIndex ?? 0) + 1
          )
        "
      >
        <BaseIconArrowRight />
      </BaseIconButton>
    </div> -->
    <BaseLoading v-if="isWritingToolsAccessLoading" class="h-full" />
    <Transition
      v-else-if="showSelfAssessment == true"
      name="pop"
      mode="out-in"
    >
      <div>
          <DocumentSelfAssessmentFormattingMessage v-if="isFormattingStep == true" />
          <DocumentSelfAssessmentBaseMessage v-if="isBaseMessageStep == true" />
          <DocumentSelfAssessmentPreCheckStep
            v-if="isActiveStepPreCheck == true"
          />
          <div
            v-if="isBaseMessageStep != true && isActiveStepPreCheck != true && isFormattingStep != true"
            key="assessment"
            class="flex flex-col gap-2"
            v-auto-animate
          >
          <div class="flex flex-row items-center gap-2 border-b pb-1 pl-4 pt-4 justify-between">
            <h2 class="text-h5 font-bold">Guided Review</h2>
            <BaseTextButton
              v-if="
                submittedDocument?.enableStudentSubmissionCreation == true ||
                writingToolsAccess.canWrite == true ||
                isCompanionMode == true
              "
              @click="backToChecklist"
              :show-spinner="isNavigatingBackToChecklist"
            >
              <div class="flex flex-row items-center gap-1 text-body-2">
                <BaseIconListChecks/>
                Back to Checklist
              </div>
            </BaseTextButton>
          </div>

          <div class="flex flex-col px-2" v-auto-animate>
            <BaseLabelBadge v-if="submittedDocument?.state != DocumentState.studentCompleted" class="mb-2">
              Follow the checklist instructions. Guided Review will not prevent you from marking an incorrect item.
            </BaseLabelBadge>
            <div
              v-if="
                diagnosticsConfiguration.enableBodyParagraphDiagnostics &&
                diagnosticsMap['paragraphs'] != undefined
              "
              class="flex flex-row items-center gap-1 border-b p-2"
            >
              <BaseIconLoading v-if="diagnosticsMap['paragraphs'].isRunning" />
              <BaseIconCircleCheck
                v-if="
                  diagnosticsMap['paragraphs'].isRunning != true &&
                  diagnosticsMap['paragraphs'].hasPassedDiagnostics
                "
              />
              <BaseIconAlert
                v-if="
                  diagnosticsMap['paragraphs'].isRunning != true &&
                  diagnosticsMap['paragraphs'].hasPassedDiagnostics != true
                "
              />
              {{ diagnosticsMap["paragraphs"].label }}
            </div>

            <div v-if="showSteps" class="flex flex-col gap-2">
              <DocumentSelfAssessmentStep
                v-for="(step, index) in stepsToShow"
                :key="step.label"
                :index="allSteps.indexOf(step)"
                :step="step"
                :is-active="activeStepIndex == allSteps.indexOf(step)"
                :class="{
                  'bg-primary/10': activeStepIndex == allSteps.indexOf(step),
                }"
              />
            </div>
            <div
              v-if="areAllStepsComplete != true"
              class="flex flex-row items-center justify-end mt-2"
            >
              <BaseButton @click="nextStep">
                <span class="flex flex-row items-center gap-1">
                  Next <BaseIconArrowRight />
                </span>
              </BaseButton>
            </div>
          </div>
          <div v-if="areAllStepsComplete" class="px-2" v-auto-animate>
            <p
              v-if="generalError != undefined"
              class="mb-2 px-2 text-danger shake"
            >
              {{ generalError }}
            </p>
            <BaseButton
              
              @click="runAssessment"
              :show-spinner="isRunningAssessment"
            >
              {{ hasRunAssessment == true ? "Reconfirm My Markings" : "Confirm My Markings" }}
            </BaseButton>
            <div v-if="hasRunAssessment == true">
        
              <div
                v-if="submittedDocument?.enableSubmissionBadges == true"
                class="border-b mt-4 mb-2 h-[1px] w-[100%] mx-auto"
              />
              <p
                v-if="
                  submittedDocument?.enableSubmissionBadges == true &&
                  showBadge != true
                "
                class="text-center"
              >
                You can still earn a badge if you fix your paper before
                submitting.
              </p>
              <div
                v-if="
                  submittedDocument?.enableSubmissionBadges == true &&
                  showBadge == true &&
                  badge != undefined
                "
                class="py-2 flex flex-col items-center gap-4"
                ref="badgeContainer"
              >
                <div class="text-center space-y-1">
                  <p class="text-h6 font-bold text-primary">Congratulations!</p>
                  <p class="flex flex-row items-center gap-1">
                    <p>
                      You earned the <strong>{{ badgeLabel }}</strong
                      >!
                    </p>
                    <BasePopover side="right">
                      <BaseIconCircleHelp />
                      <template #content>
                        <div class="p-2 flex flex-col gap-2 max-w-[400px]">
                          <p v-if="badge == SubmittedDocumentBadge.gold">
                            Great job! A Gold Scroll means you completed your
                            checklist and marked it the first time with no
                            warnings or suggestions. This shows you carefully
                            followed the writing and marking requirements before
                            submitting your paper.
                          </p>
                          <p v-if="badge == SubmittedDocumentBadge.silver">
                            Nice job! A Silver Scroll means you completed your
                            checklist and marked it with just a few warnings or
                            suggestions. With your next submission pay attention
                            to details, and you will be on your way to earning a
                            Gold Scroll.
                          </p>
                          <p v-if="badge == SubmittedDocumentBadge.bronze">
                            Good effort! A Bronze Scroll means you received a
                            lot of guidance while you completed your checklist
                            and marked your paper. This is a great learning
                            step. With your next submission, focus on the
                            directions, and you’ll be able to improve and work
                            toward earning a Silver or Gold Scroll.
                          </p>
                        </div>
                      </template>
                    </BasePopover>
                  </p>
                </div>

                <img
                  :src="`/img/badges/${badge}_scroll.png`"
                  class="max-w-[220px] w-full object-contain drop-shadow-sm transition-transform hover:scale-105"
                  alt="Achievement Badge"
                />
              </div>
              <div
                class="border-b my-4 h-[1px] w-[100%] mx-auto"
                :class="{
                  'mt-2 mb-6':
                    submittedDocument?.enableSubmissionBadges == true &&
                    showBadge == true &&
                    badge != undefined,
                }"
              />

              <BaseButton
                v-if="documentViewModeStore.isCompanionMode != true"
                @click="onSubmitWork"
                :show-spinner="isSubmitting"
                color="primary"
                class="my-2"
              >
                {{ submitButtonLabel }}
              </BaseButton>
              <DocumentCompanionExportButtons
                v-if="documentViewModeStore.isCompanionMode == true"
                class="mb-4"
              >
                <BaseOutlinedButton
                  v-if="
                    hasRunAssessment == true &&
                    submittedDocument?.state != DocumentState.studentCompleted
                  "
                  @click="runAssessment"
                  :show-spinner="isRunningAssessment"
                  class="w-full"
                >
                  Reconfirm My Markings
                </BaseOutlinedButton>
              </DocumentCompanionExportButtons>
              <div
                v-if="documentViewModeStore.isCompanionMode != true"
                class="text-center text-body-2"
              >
                An instructor will read and evaluate your paper.
              </div>
            </div>
          </div>
            </div>
      </div>
    </Transition>
    <div v-else>
      <div
        class="flex flex-row items-center mb-2 pb-2 px-4 pt-4 border-b text-h5 font-bold"
      >
        Review Submission
      </div>
      <div class="px-4">
        <BaseLabelBadge class="mb-4">
          Review your paper and make necessary corrections.
          <br />
          When you are done, click Submit.
        </BaseLabelBadge>
        <div class="flex flex-row items-center mt-2">
          <BaseButton @click="onSubmitWork" :show-spinner="isSubmitting">
            Submit
          </BaseButton>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.pop-enter-active,
.pop-leave-active {
  transition: all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.pop-enter-from {
  opacity: 0;
  transform: scale(1.3);
}

.pop-enter-to {
  opacity: 1;
  transform: scale(1);
}

.pop-leave-from {
  opacity: 1;
  transform: scale(1);
}

.pop-leave-to {
  opacity: 0;
  transform: scale(1.3);
}

@keyframes shake {
  0%,
  100% {
    transform: translateX(0);
  }
  10%,
  30%,
  50%,
  70%,
  90% {
    transform: translateX(-4px);
  }
  20%,
  40%,
  60%,
  80% {
    transform: translateX(4px);
  }
}

.shake {
  animation: shake 0.5s ease-in-out;
}
</style>
